import { UnassignedLoad } from '../types'

export type SelectedLoad = {
  loadId: number
  shipmentBookingId: number
}

export type WaypointLocationInfo = {
  waypointId: number
  locationId?: number
  locationName?: string
  address?: string
  sequence: number
}

export type ConsignmentRouteInfo = {
  shipmentBookingId: number
  waypointLocations: WaypointLocationInfo[]
  earliestScheduledTime?: string
  transportModeId?: number
  loadIds: number[]
}

export type LoadMetadata = {
  loadId: number
  shipmentBookingId: number
  trackingNumber?: string
  services?: string
  loadTypeName?: string
  loadTypeId?: number
  quantity: number
  transportModeId?: number
}

export type DestinationActionType = 'Pickup' | 'Drop-off'

export type RouteDestinationLoadAction = {
  id: number
  routeDestinationId: number
  loadId: number
  destinationActionTypeId: number
  notifyPartyId?: number
  completedDate?: string
  clearanceRequired?: boolean
}

export type RouteDestinationLoadService = {
  id: number
  routeDestinationId: number
  loadId: number
  serviceId: number
}

export type EditedLoadActionConfig = {
  loadId: number
  destinationActionTypeId: number
  clearanceRequired: boolean
  serviceIds: number[]
}

export type RouteDestination = {
  id: number
  routeId: number
  locationId: number
  locationName?: string
  address?: string
  scheduledDate: string
  sequence: number
  isUserCreated: boolean
  isBoundary: boolean
  loadActions: RouteDestinationLoadAction[]
  loadServices: RouteDestinationLoadService[]
}

export type Route = {
  id: number
  legId: number
  subcontractedTenantId?: number
  subSubcontractedTenantId?: number
  displayOrder: number
  carrierId?: number
  carrierName?: string
  transitIdentifier?: string
  scheduledDepartureDate?: string
  scheduledArrivalDate?: string
  destinations: RouteDestination[]
  loadIds: number[]
}

export type Leg = {
  id: number
  journeyId: number
  carrierId?: number
  carrierWaybillMaster?: string
  sequence: number
  startJourneyWaypointId?: number
  endJourneyWaypointId?: number
  transportModeId?: number
  fulfilmentRequired: boolean
  routes: Route[]
}

export type JourneyWaypoint = {
  id: number
  journeyId: number
  locationId: number
  locationRevision: number
  sequence: number
  isUserCreated: boolean
  isOrigin: boolean
  isFinal: boolean
  locationName?: string
  address?: string
  afterLegIndex?: number
}

export type JourneyPlannerState = {
  id?: number
  tenantId: number
  name?: string
  description?: string
  journeyStatusTypeId: number
  isApproved: boolean
  legs: Leg[]
  waypoints: JourneyWaypoint[]
  loadMetadataById: Record<number, LoadMetadata>
  isDirty: boolean
  isLoading: boolean
  isSaving: boolean
  error?: string
}

export const TransportModeId = {
  Air: 1,
  Sea: 2,
  Road: 3,
  Rail: 4,
} as const

export type TransportModeIdType = (typeof TransportModeId)[keyof typeof TransportModeId]

export const getTransportModeIdFromName = (name?: string): number | undefined => {
  if (!name) return undefined
  const normalizedName = name.toLowerCase()
  switch (normalizedName) {
    case 'road':
      return TransportModeId.Road
    case 'sea':
      return TransportModeId.Sea
    case 'air':
      return TransportModeId.Air
    case 'rail':
      return TransportModeId.Rail
    default:
      return undefined
  }
}

export const DestinationActionTypeId = {
  Pickup: 1,
  DropOff: 2,
} as const

export type DestinationActionTypeIdType =
  (typeof DestinationActionTypeId)[keyof typeof DestinationActionTypeId]

export type DestinationLoadActionConfig = {
  loadId: number
  destinationActionTypeId: number
  clearanceRequired: boolean
  serviceIds: number[]
}

export const createInitialPlannerState = (tenantId: number): JourneyPlannerState => ({
  tenantId,
  journeyStatusTypeId: 1,
  isApproved: false,
  legs: [],
  waypoints: [],
  loadMetadataById: {},
  isDirty: false,
  isLoading: false,
  isSaving: false,
})

export const buildLoadMetadataFromUnassignedLoads = (
  loads: UnassignedLoad[]
): Record<number, LoadMetadata> => {
  return loads.reduce(
    (acc, load) => {
      acc[load.loadId] = {
        loadId: load.loadId,
        shipmentBookingId: load.shipmentBookingId,
        trackingNumber: load.trackingNumber,
        services: load.services,
        loadTypeId: load.loadTypeId,
        loadTypeName: load.loadTypeName,
        quantity: load.quantity,
      }
      return acc
    },
    {} as Record<number, LoadMetadata>
  )
}
